import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { PermissionService } from '../security/permission.service';
import { app_permissions } from '../security/app-permissions';
import { WASenderAccount, WASenderRateProfile } from './wasender.model';
import { WASenderService } from './wasender.service';

@Component({
  selector: 'app-wasender-setup',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    BreadcrumbModule,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    ProgressBarModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
    ToggleSwitchModule,
    TooltipModule,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './wasender-setup.component.html',
  styleUrl: './wasender-setup.component.css'
})
export class WASenderSetupComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly wasenderService = inject(WASenderService);
  private readonly messageService = inject(MessageService);
  private readonly permissionService = inject(PermissionService);

  breadcrumbItems: MenuItem[] = [
    { label: 'Administration' },
    { label: 'WhatsApp Numbers', routerLink: '/wasender/setup' }
  ];
  homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/' };

  accounts: WASenderAccount[] = [];
  isLoading = false;
  accountDialogVisible = false;
  testingAccountId?: number;
  canManage = false;
  accountForm!: FormGroup;

  readonly rateProfiles: { label: string; value: WASenderRateProfile; description: string }[] = [
    { label: 'Account Protection', value: 'ACCOUNT_PROTECTION', description: 'One message every 5 seconds.' },
    { label: 'Trial', value: 'TRIAL', description: 'One message per minute with 50 per day.' },
    { label: 'Paid', value: 'PAID', description: 'Up to 256 messages per minute.' },
    { label: 'Custom', value: 'CUSTOM', description: 'Tenant-specific spacing and daily cap.' }
  ];

  ngOnInit(): void {
    this.canManage = this.permissionService.hasPermission(app_permissions.wasender.setup.manage);
    this.initForm();
    this.loadAccounts();
  }

  initForm(): void {
    this.accountForm = this.fb.group({
      id: [null],
      displayName: ['', Validators.required],
      phoneNumber: ['', Validators.required],
      sessionId: ['', Validators.required],
      apiKey: [''],
      webhookSecret: [''],
      active: [true],
      defaultAccount: [false],
      rateProfile: ['ACCOUNT_PROTECTION', Validators.required],
      customMinSpacingSeconds: [5],
      customDailyCap: [null]
    });
  }

  loadAccounts(): void {
    this.isLoading = true;
    this.wasenderService.getAccounts().subscribe({
      next: accounts => {
        this.accounts = accounts;
        this.isLoading = false;
      },
      error: () => {
        this.accounts = [];
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'WhatsApp Numbers', detail: 'Unable to load WASender accounts.' });
      }
    });
  }

  openNew(): void {
    if (!this.canManage) return;
    this.initForm();
    this.accountDialogVisible = true;
  }

  editAccount(account: WASenderAccount): void {
    if (!this.canManage) return;
    this.accountForm.patchValue({
      id: account.id,
      displayName: account.displayName,
      phoneNumber: account.phoneNumber,
      sessionId: account.sessionId,
      apiKey: '',
      webhookSecret: '',
      active: account.active !== false,
      defaultAccount: account.defaultAccount === true,
      rateProfile: account.rateProfile || 'ACCOUNT_PROTECTION',
      customMinSpacingSeconds: account.customMinSpacingSeconds || 5,
      customDailyCap: account.customDailyCap || null
    });
    this.accountDialogVisible = true;
  }

  saveAccount(): void {
    if (!this.canManage) {
      return;
    }
    if (this.accountForm.invalid) {
      this.accountForm.markAllAsTouched();
      return;
    }
    const value = this.accountForm.value;
    if (!value.id && (!String(value.apiKey || '').trim() || !String(value.webhookSecret || '').trim())) {
      this.messageService.add({ severity: 'warn', summary: 'Credentials Required', detail: 'API key and webhook secret are required for a new number.' });
      return;
    }

    const payload: WASenderAccount = {
      id: value.id,
      displayName: value.displayName,
      phoneNumber: value.phoneNumber,
      sessionId: value.sessionId,
      apiKey: String(value.apiKey || '').trim() || undefined,
      webhookSecret: String(value.webhookSecret || '').trim() || undefined,
      active: value.active,
      defaultAccount: value.defaultAccount,
      rateProfile: value.rateProfile,
      customMinSpacingSeconds: value.rateProfile === 'CUSTOM' ? value.customMinSpacingSeconds : undefined,
      customDailyCap: value.rateProfile === 'CUSTOM' ? value.customDailyCap : undefined
    };

    this.isLoading = true;
    this.wasenderService.saveAccount(payload).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'WhatsApp Number', detail: value.id ? 'Account updated.' : 'Account created.' });
        this.accountDialogVisible = false;
        this.loadAccounts();
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: 'Unable to save the WASender account.' });
      }
    });
  }

  setDefault(account: WASenderAccount): void {
    if (!this.canManage || !account.id) return;
    this.isLoading = true;
    this.wasenderService.setDefaultAccount(account.id).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Default Number', detail: `${account.displayName} is now the default sender.` });
        this.loadAccounts();
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Default Failed', detail: 'Unable to set the default number.' });
      }
    });
  }

  testAccount(account: WASenderAccount): void {
    if (!this.canManage || !account.id) return;
    this.testingAccountId = account.id;
    this.wasenderService.testAccount(account.id).subscribe({
      next: response => {
        this.testingAccountId = undefined;
        this.messageService.add({
          severity: 'success',
          summary: 'Connection Test Queued',
          detail: response.message || 'A test message was queued through Cloud Tasks.'
        });
      },
      error: () => {
        this.testingAccountId = undefined;
        this.messageService.add({ severity: 'error', summary: 'Connection Test Failed', detail: 'Unable to queue the test message.' });
      }
    });
  }

  rateProfileDescription(value?: WASenderRateProfile): string {
    return this.rateProfiles.find(profile => profile.value === value)?.description || '';
  }

  statusSeverity(account: WASenderAccount): any {
    if (account.active === false) return 'secondary';
    return account.defaultAccount ? 'success' : 'info';
  }

  statusLabel(account: WASenderAccount): string {
    if (account.active === false) return 'Inactive';
    return account.defaultAccount ? 'Default' : 'Active';
  }

  showCustomLimits(): boolean {
    return this.accountForm?.get('rateProfile')?.value === 'CUSTOM';
  }
}
